import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckIcon, XIcon } from 'lucide-react';
import { useAuth } from '../hooks';
import { ROUTES } from '../routes';
import { Cursor, MockdLogo } from '../components/common';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const { verifyEmail, error } = useAuth();

  const token = searchParams.get('token');
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;

    verifyEmail(token)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token, verifyEmail]);

  if (!token) {
    return (
      <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="card bg-base-100 p-6">
            <div className="w-12 h-12 mx-auto mb-4 bg-error/20 rounded-full flex items-center justify-center">
              <XIcon className="size-6 text-error" />
            </div>
            <h1 className="text-base-content mb-2">Invalid Link</h1>
            <p className="text-base-content/80 mb-6">This verification link is invalid.</p>
            <Link to={ROUTES.dashboard()} className="btn btn-primary w-full">
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <MockdLogo size="md" className="mx-auto" />
        </div>

        <div className="card bg-base-100 p-6">
          {status === 'verifying' && (
            <>
              <Cursor size="lg" className="mx-auto mb-4" />
              <h1 className="text-base-content mb-2">Verifying</h1>
              <p className="text-base-content/80">Please wait while we verify your email...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 mx-auto mb-4 bg-success/20 rounded-full flex items-center justify-center">
                <CheckIcon className="size-6 text-success" />
              </div>
              <h1 className="text-base-content mb-2">Email verified</h1>
              <p className="text-base-content/80 mb-6">Thanks for confirming your email address.</p>
              <Link to={ROUTES.dashboard()} className="btn btn-primary w-full">
                Go to dashboard
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-12 h-12 mx-auto mb-4 bg-error/20 rounded-full flex items-center justify-center">
                <XIcon className="size-6 text-error" />
              </div>
              <h1 className="text-base-content mb-2">Verification failed</h1>
              <p className="text-base-content/80 mb-6">{error || 'This verification link is invalid or has expired.'}</p>
              <Link to={ROUTES.dashboard()} className="btn btn-primary w-full">
                Go to dashboard
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
